import {
  Flex,
  PasswordInput,
  PasswordRequirements,
  PasswordStrength,
  Text,
} from '@components';
import { useUpdatePasswordMutation } from '@features/api/userSlice';
import { yupResolver } from '@hookform/resolvers/yup';
import { calculatePasswordStrength } from '@pages/SignUp/SignUp.utils';
import { registerInput } from '@utils/reactHookForm';
import { getErrorProps } from '@utils/validation/validationFunctions';
import { updatePasswordFormSchema } from '@validationSchemas/updateProfile';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { StyledButton } from './UpdatePassword.styles';
import { PasswordFormData, UpdatePasswordProps } from './UpdatePassword.types';
import { toast } from 'react-toastify';
import { useTheme } from '@mui/material';
import { ReactNode } from 'react';

const UpdatePassword: React.FC<UpdatePasswordProps> = ({ onClose }) => {
  const intl = useIntl();
  const theme = useTheme();

  const [updatePasswordMutation] = useUpdatePasswordMutation();

  const {
    register,
    watch,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<PasswordFormData>({
    resolver: yupResolver(updatePasswordFormSchema),
    mode: 'onChange',
    shouldFocusError: false,
  });
  const password = watch('password');
  const passwordsDifferent =
    errors.confirmPassword?.type === 'oneOf' && password;

  const onSubmit = async (data: PasswordFormData) => {
    try {
      await updatePasswordMutation({
        newPassword: data.password,
        currentPassword: data.oldPassword,
      }).unwrap();

      onClose();
      toast(intl.formatMessage({ id: 'profile.passwordChangedMessage' }), {
        type: 'success',
      });
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Flex flexDirection='column' gap={12} mt={10}>
        <PasswordInput
          label={intl.formatMessage({ id: 'misc.oldPassword' })}
          autoComplete='old-password'
          inputProps={{
            'data-testid': 'old-password',
          }}
          {...registerInput(register, 'oldPassword')}
          {...getErrorProps(intl, errors.oldPassword)}
        />
        <PasswordInput
          label={intl.formatMessage({ id: 'misc.password' })}
          autoComplete='new-password'
          {...registerInput(register, 'password')}
          inputProps={{
            'data-testid': 'new-password',
          }}
          error={
            errors.password?.type === 'required'
              ? getErrorProps(intl, errors.password).error
              : undefined
          }
          helperText={
            errors.password?.type === 'required'
              ? getErrorProps(intl, errors.password).helperText
              : undefined
          }
        />
        <PasswordInput
          label={intl.formatMessage({ id: 'misc.confirmNewPassword' })}
          autoComplete='new-password'
          inputProps={{
            'data-testid': 'confirm-new-password',
          }}
          {...registerInput(register, 'confirmPassword')}
          error={
            errors.confirmPassword?.type === 'required'
              ? getErrorProps(intl, errors.confirmPassword).error
              : undefined
          }
          helperText={
            errors.confirmPassword?.type === 'required'
              ? getErrorProps(intl, errors.confirmPassword).helperText
              : undefined
          }
        />
        <PasswordStrength
          currentLevel={calculatePasswordStrength(errors.password, password)}
          error={
            passwordsDifferent
              ? intl.formatMessage({
                  id: 'signUp.errors.passwordsNotMatching',
                })
              : undefined
          }
        />
        {errors.password && errors.password?.type === 'matches' && (
          <Text variant='h6' fontWeight='fontWeightSemiBold' marginTop={2}>
            {intl.formatMessage({
              id: errors.password.message ?? '',
            })}
          </Text>
        )}
        {errors.password &&
          errors.password?.type !== 'required' &&
          errors.password?.type !== 'matches' && (
            <PasswordRequirements
              errors={errors.password.message?.split('.')}
            />
          )}
        <Text
          variant='h5'
          fontWeight='fontWeightRegular'
          sx={{ color: theme.palette.black[500] }}
        >
          <FormattedMessage
            id='profile.updatePasswordNote'
            values={{
              b: (chunks: ReactNode[]): ReactNode => (
                <Text
                  component='span'
                  variant='h5'
                  fontWeight='fontWeightSemiBold'
                  sx={{ display: 'inline-flex' }}
                >
                  {chunks}
                </Text>
              ),
            }}
          />
        </Text>
        <StyledButton
          disabled={!isValid}
          type='submit'
          data-testid='reset-password-button'
        >
          <FormattedMessage id='misc.save' />
        </StyledButton>
      </Flex>
    </form>
  );
};

export default UpdatePassword;
